import React, { useEffect, useState } from "react";
import { Panel } from "../components/Panel";
import { usePlayer } from "../context/PlayerContext";
import { api, apiErr } from "../api/client";
import { CosmeticIcon } from "../components/CosmeticIcon";
import { Coins, Loader2, Boxes, Hammer, Zap, Recycle, Sparkles, Lock, Flame } from "lucide-react";
import { Sound } from "../game/audio";
import { toast } from "sonner";

const RARITY = { common: "#22D3EE", rare: "#34D399", epic: "#A78BFA", legendary: "#FFD700" };
const RARITY_LABEL = { common: "Common", rare: "Rare", epic: "Epic", legendary: "Legendary" };
const FORGE_ORDER = ["common", "rare", "epic", "legendary"];

export default function Salvage({ onClose, onNeedShop }) {
  const { profile, applyUser } = usePlayer();
  const [tab, setTab] = useState("vaults");
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cracking, setCracking] = useState(null); // tier being cracked (for anim)
  const [reward, setReward] = useState(null); // last crack/forge reward
  const [forgeRarity, setForgeRarity] = useState("rare");
  const [overcharge, setOvercharge] = useState(false);

  const load = () => api.salvage().then(({ data }) => setState(data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const scrap = profile?.scrap || 0;
  const cores = profile?.cores || 0;
  const charge = profile?.salvageCharge || 0;
  const vaults = profile?.vaults || { rusty: 0, reinforced: 0, prismatic: 0 };

  const crack = async (tier, instant) => {
    if (busy) return;
    setBusy(true); setCracking(tier); setReward(null);
    Sound.engine?.(1);
    try {
      const { data } = await api.salvageCrack(tier, instant);
      await new Promise((r) => setTimeout(r, 850));
      Sound.crash?.(data.rarity === "legendary" ? 2 : 1.4);
      if (navigator.vibrate) navigator.vibrate(data.rarity === "legendary" ? [40, 30, 60] : 30);
      applyUser(data.user);
      setReward({ kind: "vault", ...data });
      load();
    } catch (e) { toast.error(apiErr(e, "Could not crack the vault.")); }
    finally { setBusy(false); setCracking(null); }
  };

  const forge = async () => {
    if (busy) return;
    setBusy(true); setReward(null);
    Sound.click?.();
    try {
      const { data } = await api.salvageForge(forgeRarity, overcharge);
      await new Promise((r) => setTimeout(r, 700));
      Sound.crash?.(data.upgraded ? 2 : 1.2);
      applyUser(data.user);
      setReward({ kind: "forge", ...data });
      if (data.upgraded) toast.success(`⚡ OVERCHARGE! Bumped up to ${RARITY_LABEL[data.rarity]}!`);
      load();
    } catch (e) { toast.error(apiErr(e, "Forge failed.")); }
    finally { setBusy(false); }
  };

  return (
    <Panel title="Salvage Bay" onClose={onClose} wide>
      {/* resource bar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="salvage-resources">
        <Chip icon={Coins} color="#FBBF24" label="Coins" val={(profile?.coins || 0).toLocaleString()} testid="salvage-coins" />
        <Chip icon={Recycle} color="#34D399" label="Scrap" val={scrap.toLocaleString()} testid="salvage-scrap" />
        <Chip icon={Sparkles} color="#A78BFA" label="Cores" val={cores.toLocaleString()} testid="salvage-cores" />
        <div className="ml-auto flex gap-1 bg-slate-900 rounded-xl p-1">
          <button onClick={() => setTab("vaults")} className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 ${tab === "vaults" ? "bg-amber-500 text-black" : "text-slate-300"}`} data-testid="salvage-tab-vaults"><Boxes size={13} /> Vaults</button>
          <button onClick={() => setTab("forge")} className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 ${tab === "forge" ? "bg-amber-500 text-black" : "text-slate-300"}`} data-testid="salvage-tab-forge"><Hammer size={13} /> Forge</button>
        </div>
      </div>

      {/* reward reveal */}
      {reward && (
        <div className="card p-3 mb-4 flex items-center gap-3 burst-in" style={{ borderColor: RARITY[reward.rarity], borderWidth: 2 }} data-testid="salvage-reward">
          {reward.cosmetic ? (
            <CosmeticIcon type={reward.cosmetic.type} color={reward.cosmetic.color} size={46} />
          ) : (
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "rgba(52,211,153,0.15)" }}><Recycle size={24} className="text-emerald-400" /></div>
          )}
          <div className="flex-1">
            <div className="font-black text-white flex items-center gap-2">
              {reward.cosmetic ? reward.cosmetic.name : "Duplicate → Scrap"}
              <span className="text-[10px] uppercase font-black px-1.5 py-0.5 rounded" style={{ color: RARITY[reward.rarity], background: "rgba(255,255,255,0.06)" }}>{RARITY_LABEL[reward.rarity]}</span>
              {reward.upgraded && <span className="text-[10px] uppercase font-black text-amber-300 flex items-center gap-0.5"><Zap size={11} /> Upgraded</span>}
            </div>
            <div className="text-xs text-slate-300 flex items-center gap-3 mt-0.5">
              {reward.coins ? <span className="text-amber-300 flex items-center gap-1"><Coins size={12} /> +{reward.coins}</span> : null}
              {reward.scrap ? <span className="text-emerald-300 flex items-center gap-1"><Recycle size={12} /> +{reward.scrap}</span> : null}
              {reward.cores ? <span className="text-violet-300 flex items-center gap-1"><Sparkles size={12} /> +{reward.cores}</span> : null}
              {reward.cosmetic && <span className="text-slate-400">Sent to Garage</span>}
            </div>
          </div>
        </div>
      )}

      {/* ---------- VAULTS ---------- */}
      {tab === "vaults" && state && (
        <>
          <div className="text-[11px] text-slate-400 mb-3">Vaults charge up as you <b className="text-cyan-300">smash objects in matches</b>. Crack at full charge for the best loot — or pay coins to crack it now for a weaker pull.</div>
          <div className="grid md:grid-cols-3 gap-3">
            {state.order.map((tier) => {
              const cfg = state.tiers[tier];
              const owned = vaults[tier] || 0;
              const need = cfg.chargeNeeded;
              const pct = Math.min(100, (charge / need) * 100);
              const canCharged = owned > 0 && charge >= need;
              const canInstant = owned > 0 && (profile?.coins || 0) >= cfg.instantCost;
              return (
                <div key={tier} className={`card cos-tile p-4 flex flex-col items-center ${cracking === tier ? "crate-shake" : ""}`} style={{ borderColor: `${cfg.color}66`, borderWidth: 2 }} data-testid={`vault-${tier}`}>
                  <div className="relative">
                    <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-1" style={{ background: `linear-gradient(135deg, ${cfg.color}, ${cfg.color}22)`, boxShadow: `0 0 26px ${cfg.color}66` }}>
                      <Boxes size={44} style={{ color: "#0b0f1a" }} />
                    </div>
                    <span className="absolute -top-1 -right-1 min-w-6 h-6 px-1.5 rounded-full bg-slate-900 border-2 flex items-center justify-center text-xs font-black text-white" style={{ borderColor: cfg.color }} data-testid={`vault-count-${tier}`}>{owned}</span>
                  </div>
                  <div className="font-black text-white mt-1" style={{ color: cfg.color }}>{cfg.name}</div>
                  {/* charge meter */}
                  <div className="w-full mt-2">
                    <div className="flex justify-between text-[10px] text-slate-400 mb-0.5"><span>Charge</span><span>{Math.min(charge, need)}/{need}</span></div>
                    <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: cfg.color, boxShadow: pct >= 100 ? `0 0 10px ${cfg.color}` : "none" }} data-testid={`vault-charge-${tier}`} />
                    </div>
                  </div>
                  <button onClick={() => crack(tier, false)} disabled={!canCharged || busy}
                    className="btn-gold w-full py-2 mt-3 text-sm flex items-center justify-center gap-1 disabled:opacity-40" data-testid={`vault-crack-${tier}`}>
                    {busy && cracking === tier ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {charge >= need ? "CRACK (Full)" : "Charging…"}
                  </button>
                  <button onClick={() => crack(tier, true)} disabled={!canInstant || busy}
                    className="btn-ghost w-full py-1.5 mt-1.5 text-xs flex items-center justify-center gap-1 border-amber-500/40 text-amber-300 disabled:opacity-40" data-testid={`vault-instant-${tier}`}>
                    <Coins size={12} /> Crack now — {cfg.instantCost.toLocaleString()}
                  </button>
                  {owned === 0 && <div className="text-[10px] text-slate-500 mt-1.5 flex items-center gap-1"><Lock size={10} /> Win matches to earn one</div>}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ---------- FORGE ---------- */}
      {tab === "forge" && state && (
        <>
          <div className="text-[11px] text-slate-400 mb-3">Smelt your <b className="text-emerald-300">Scrap</b> into a fresh cosmetic of a chosen rarity. Spend <b className="text-violet-300">Cores</b> to <b className="text-amber-300">Overcharge</b> — a {Math.round(state.overchargeChance * 100)}% shot to bump the result up a whole tier.</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            {FORGE_ORDER.map((r) => {
              const cost = state.forgeCost[r];
              const sel = forgeRarity === r;
              const afford = scrap >= cost;
              return (
                <button key={r} onClick={() => setForgeRarity(r)} disabled={busy}
                  className={`card p-3 flex flex-col items-center gap-1 transition-all ${sel ? "border-2" : "opacity-80 hover:opacity-100"}`}
                  style={sel ? { borderColor: RARITY[r], boxShadow: `0 0 16px ${RARITY[r]}44` } : {}} data-testid={`forge-rarity-${r}`}>
                  <div className="w-8 h-8 rounded-full" style={{ background: RARITY[r], boxShadow: `0 0 12px ${RARITY[r]}` }} />
                  <div className="text-xs font-black" style={{ color: RARITY[r] }}>{RARITY_LABEL[r]}</div>
                  <div className={`text-[11px] flex items-center gap-0.5 ${afford ? "text-emerald-300" : "text-slate-500"}`}><Recycle size={11} /> {cost.toLocaleString()}</div>
                </button>
              );
            })}
          </div>

          <div className="card p-4 flex flex-col items-center" data-testid="forge-stage">
            <div className={`w-24 h-24 rounded-2xl flex items-center justify-center ${busy ? "crate-shake" : ""}`} style={{ background: `radial-gradient(circle, ${RARITY[forgeRarity]}33, transparent 70%)` }}>
              <Flame size={54} style={{ color: RARITY[forgeRarity], filter: `drop-shadow(0 0 10px ${RARITY[forgeRarity]})` }} />
            </div>
            <label className="flex items-center gap-2 mt-3 cursor-pointer select-none" data-testid="forge-overcharge-toggle">
              <input type="checkbox" checked={overcharge} onChange={(e) => setOvercharge(e.target.checked)} disabled={forgeRarity === "legendary"} className="accent-amber-500 w-4 h-4" />
              <span className={`text-sm font-bold flex items-center gap-1 ${forgeRarity === "legendary" ? "text-slate-600" : "text-amber-300"}`}>
                <Zap size={14} /> Overcharge ({state.overchargeCores} <Sparkles size={12} className="inline" />)
                {forgeRarity === "legendary" && <span className="text-[10px] text-slate-500">— max tier</span>}
              </span>
            </label>
          </div>

          <button onClick={forge} disabled={busy || scrap < state.forgeCost[forgeRarity] || (overcharge && cores < state.overchargeCores)}
            className="btn-gold w-full py-3.5 mt-3 text-lg flex items-center justify-center gap-2 disabled:opacity-50" data-testid="forge-run">
            {busy ? <Loader2 size={20} className="animate-spin" /> : <Hammer size={20} />} FORGE {RARITY_LABEL[forgeRarity].toUpperCase()}
          </button>
          {scrap < state.forgeCost[forgeRarity] && <div className="text-center text-[11px] text-slate-500 mt-2">Not enough Scrap — smash objects in matches or crack vault duplicates to earn more.</div>}
        </>
      )}

      {!state && <div className="text-slate-500 text-sm py-10 text-center flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading Salvage Bay…</div>}
    </Panel>
  );
}

function Chip({ icon: Icon, color, label, val, testid }) {
  return (
    <div className="card px-3 py-1.5 flex items-center gap-1.5" data-testid={testid}>
      <Icon size={16} style={{ color }} />
      <span className="font-black text-white text-sm">{val}</span>
      <span className="text-[10px] text-slate-400 uppercase hidden sm:inline">{label}</span>
    </div>
  );
}
